import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Divider,
  Stack,
  Chip
} from '@mui/material';
import {
  AutoAwesome as AgentIcon,
  PlayArrow as RunIcon,
  Check as ApplyIcon
} from '@mui/icons-material';
import api from '../../api/axios';

/**
 * In-editor AI agent for custom_code: understand → plan → generate → backtest.
 */
export default function CustomCodeAgentPanel({
  projectId,
  companyId,
  flowData,
  step,
  availableVariables = [],
  onApply
}) {
  const [request, setRequest] = useState('');
  const [plan, setPlan] = useState(null);
  const [planText, setPlanText] = useState('');
  const [draft, setDraft] = useState(null);
  const [backtestReport, setBacktestReport] = useState(null);
  const [sampleVarsJson, setSampleVarsJson] = useState('{\n  "extractedText": "Sample PO-123 total 100"\n}');
  const [allowLiveHttp, setAllowLiveHttp] = useState(false);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const priorVars = availableVariables.length
    ? availableVariables
    : (flowData?.steps || [])
        .filter(s => s.id !== step?.id && s.type !== 'end')
        .flatMap(s => {
          if (s.type === 'custom_code') {
            return (s.config?.outputVariables || []).map(o => o.name);
          }
          if (s.type === 'data_processing') {
            return [s.config?.outputVariable || 'extractedText'];
          }
          return [];
        })
        .filter(Boolean);

  const runPlan = async () => {
    setError(null);
    setBusy('plan');
    setDraft(null);
    setBacktestReport(null);
    try {
      const { data } = await api.post('/custom-code-agent/plan', {
        projectId,
        companyId,
        userRequest: request,
        availableVariables: priorVars,
        currentConfig: step?.config || {},
        flowSummary: (flowData?.steps || []).map(s => ({ name: s.name, type: s.type }))
      });
      setPlan(data.plan);
      setPlanText(data.plan?.planText || data.plan?.summary || JSON.stringify(data.plan, null, 2));
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Plan failed');
    } finally {
      setBusy(null);
    }
  };

  const runGenerate = async () => {
    setError(null);
    setBusy('generate');
    setBacktestReport(null);
    try {
      const { data } = await api.post('/custom-code-agent/generate', {
        projectId,
        companyId,
        userRequest: request,
        plan: { ...(plan || {}), planText },
        availableVariables: priorVars,
        currentConfig: step?.config || {}
      });
      setDraft(data.result);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Generate failed');
    } finally {
      setBusy(null);
    }
  };

  const runBacktest = async () => {
    if (!draft?.code) {
      setError('Generate a script first');
      return;
    }
    setError(null);
    setBusy('backtest');
    let sampleVars = {};
    try {
      sampleVars = JSON.parse(sampleVarsJson || '{}');
    } catch {
      setError('Sample variables JSON is invalid');
      setBusy(null);
      return;
    }
    try {
      const { data } = await api.post('/custom-code-agent/backtest', {
        projectId,
        companyId,
        code: draft.code,
        outputVariables: draft.outputVariables,
        bindings: draft.bindings,
        timeoutMs: draft.timeoutMs,
        sampleVars,
        allowLiveHttp,
        maxRetries: 2,
        userRequest: request,
        plan: { ...(plan || {}), planText }
      });
      setBacktestReport(data.report);
      if (data.result) {
        setDraft(data.result);
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Backtest failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Alert severity="info" icon={<AgentIcon />}>
        Describe what the script should do. The agent will propose a plan, generate code + resource
        bindings (models, API auth), then backtest.
      </Alert>

      <TextField
        label="What should this script do?"
        multiline
        minRows={3}
        fullWidth
        value={request}
        onChange={(e) => setRequest(e.target.value)}
        placeholder="e.g. Parse extractedText into line items, call an LLM to normalize product names, POST to our webhook"
      />

      {priorVars.length > 0 && (
        <Box>
          <Typography variant="caption" color="text.secondary">Available variables: </Typography>
          {priorVars.slice(0, 20).map(v => (
            <Chip key={v} size="small" label={typeof v === 'string' ? v : v.name} sx={{ mr: 0.5, mb: 0.5 }} />
          ))}
        </Box>
      )}

      <Button
        variant="contained"
        onClick={runPlan}
        disabled={!request.trim() || !!busy}
        startIcon={busy === 'plan' ? <CircularProgress size={16} /> : <AgentIcon />}
      >
        1. Understand & Plan
      </Button>

      {plan && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Plan (edit before coding)</Typography>
          <TextField
            multiline
            minRows={6}
            fullWidth
            value={planText}
            onChange={(e) => setPlanText(e.target.value)}
          />
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button
              variant="contained"
              color="secondary"
              onClick={runGenerate}
              disabled={!!busy}
              startIcon={busy === 'generate' ? <CircularProgress size={16} /> : null}
            >
              2. Generate Code
            </Button>
          </Stack>
        </Paper>
      )}

      {draft && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Generated Script</Typography>
          <Box
            component="pre"
            sx={{
              maxHeight: 200,
              overflow: 'auto',
              bgcolor: 'grey.900',
              color: 'grey.100',
              p: 1,
              borderRadius: 1,
              fontSize: 11
            }}
          >
            {draft.code}
          </Box>
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            Outputs: {(draft.outputVariables || []).map(o => o.name).join(', ') || '(none)'}
          </Typography>
          <Typography variant="caption" display="block">
            Bindings: LLM {(draft.bindings?.llm || []).length}, Vision {(draft.bindings?.vision || []).length}, HTTP {(draft.bindings?.http || []).length}
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom>3. Backtest</Typography>
          <TextField
            label="Sample vars (JSON)"
            multiline
            minRows={3}
            fullWidth
            value={sampleVarsJson}
            onChange={(e) => setSampleVarsJson(e.target.value)}
            sx={{ mb: 1 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={allowLiveHttp}
                onChange={(e) => setAllowLiveHttp(e.target.checked)}
              />
            }
            label="Allow live HTTP (default: mocked)"
          />
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button
              variant="outlined"
              onClick={runBacktest}
              disabled={!!busy}
              startIcon={busy === 'backtest' ? <CircularProgress size={16} /> : <RunIcon />}
            >
              Run Backtest
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<ApplyIcon />}
              onClick={() => onApply?.(draft)}
            >
              Apply to Node
            </Button>
          </Stack>
        </Paper>
      )}

      {backtestReport && (
        <Alert severity={backtestReport.passed ? 'success' : 'warning'}>
          <Typography variant="body2">{backtestReport.summary}</Typography>
          {backtestReport.warnings?.length > 0 && (
            <Typography variant="caption" component="div">
              Warnings: {backtestReport.warnings.join('; ')}
            </Typography>
          )}
          {backtestReport.errors?.length > 0 && (
            <Typography variant="caption" component="div" color="error">
              Errors: {backtestReport.errors.join('; ')}
            </Typography>
          )}
          {backtestReport.outputs && (
            <Box component="pre" sx={{ fontSize: 11, mt: 1, maxHeight: 120, overflow: 'auto' }}>
              {JSON.stringify(backtestReport.outputs, null, 2)}
            </Box>
          )}
        </Alert>
      )}

      {error && <Alert severity="error">{error}</Alert>}
    </Box>
  );
}
